import type { EnemyDef, EnemyEffect, EnemyTypeId, ZoneDef } from '../core/types';
import { BALANCE } from './balance';

export type { EnemyEffect, ZoneDef };

/**
 * Bestiaire complet — **COMMENTS v0.7.0 §1** : 10 zones de 5 ennemis (4
 * ordinaires + le boss de l'Invasion), recopiées telles quelles des tables du
 * game designer (PV, or, effet, condition d'apparition).
 *
 * Lecture des colonnes — arbitrage **A25** :
 *  - « Zone N » = **niveau d'expédition N** ; au-delà du niveau 10, la zone 10
 *    se répète (`zoneForLevel`), la courbe de PV continuant de monter ;
 *  - « Condition d'apparition » = `minDay`, le **jour minimal** du niveau où
 *    l'ennemi devient tirable (jour 6 = Invasion : tout le bestiaire de la zone
 *    est tirable) ;
 *  - les boss valent tous **48 PV / 50 or** et ne sortent jamais du pool
 *    aléatoire (`minDay: BALANCE.invasionDay`).
 *
 * v0.49.0 (COMMENTS v0.48.1 §1.8) : `effect` devient `effects` (une liste,
 * un seul élément pour les ennemis ordinaires) et neuf boss gagnent des effets
 * — constantes `vampireLord…`, `blackWidow…`, `orcChief…`, `mermaidQueen…`,
 * `mechaGoblin…`, `sandWorm…`, `cursedKing…`, `tricorn…` de `balance.ts`. Le
 * Sanglier Alpha reste sans effet.
 *
 * Les poids d'apparition 10/8/6/6/4 de la v0.7.0 disparaissent : la table du GD
 * n'en donne aucun, le pool d'un jour est donc tiré à **poids égaux** (§F).
 *
 * Les valeurs par ennemi (chances, pourcentages) vivent ici ; les constantes
 * partagées (durée d'assommement, secondes de l'Ingénieur…) restent dans
 * `balance.ts` et sont référencées ci-dessous pour n'avoir qu'une source.
 *
 * v0.9.0 (COMMENTS v0.8.0 §2.2) : les ennemis qui régénèrent des PV étaient
 * trop puissants. Leur effet est divisé par deux — **Vampire** et **Requin**
 * 10 % → 5 % de vie régénérée par seconde, **Amalgame de chair** et **Plante
 * carnivore** 15 % → 7,5 %. Le Sanglier (5 %) et le reste du bestiaire ne
 * bougent pas.
 */

/** Zone 1 — Alentours du Village. */
const ZONE_1: EnemyDef[] = [
  { id: 'rabbit', name: 'Lapin', baseHp: 4, baseGold: 3, zone: 1, minDay: 1 },
  {
    id: 'slime',
    name: 'Slime',
    baseHp: 5,
    baseGold: 4,
    zone: 1,
    minDay: 1,
    effects: [{ kind: 'revive', percent: 0.15 }],
  },
  {
    id: 'wolf',
    name: 'Loup',
    baseHp: 5,
    baseGold: 4,
    zone: 1,
    minDay: 2,
    effects: [{ kind: 'dodge', chance: 0.1 }],
  },
  {
    id: 'boar',
    name: 'Sanglier',
    baseHp: 11,
    baseGold: 7,
    zone: 1,
    minDay: 3,
    effects: [{ kind: 'regen', percentPerSecond: 0.05 }],
  },
  { id: 'boarAlpha', name: 'Sanglier Alpha', baseHp: 48, baseGold: 50, zone: 1, minDay: 6, boss: true },
];

/** Zone 2 — Cimetière de la forêt. */
const ZONE_2: EnemyDef[] = [
  { id: 'skeleton', name: 'Squelette', baseHp: 7, baseGold: 5, zone: 2, minDay: 1 },
  {
    id: 'skeletonArcher',
    name: 'Squelette Archer',
    baseHp: 5,
    baseGold: 4,
    zone: 2,
    minDay: 1,
    effects: [{ kind: 'dodge', chance: 0.2 }],
  },
  {
    id: 'zombie',
    name: 'Zombie',
    baseHp: 9,
    baseGold: 6,
    zone: 2,
    minDay: 2,
    effects: [{ kind: 'revive', percent: 0.2 }],
  },
  {
    id: 'shieldSkeleton',
    name: 'Squelette à bouclier',
    baseHp: 11,
    baseGold: 7,
    zone: 2,
    minDay: 3,
    effects: [{ kind: 'shield' }],
  },
  {
    id: 'skeletonKing',
    name: 'Roi squelette',
    baseHp: 48,
    baseGold: 50,
    zone: 2,
    minDay: 6,
    boss: true,
    // v0.49.0 (COMMENTS v0.48.1 §1.8).
    effects: [
      { kind: 'shield' },
    ],
  },
];

/** Zone 3 — Forêt aux vampires. */
const ZONE_3: EnemyDef[] = [
  { id: 'bat', name: 'Chauve-souris', baseHp: 5, baseGold: 4, zone: 3, minDay: 1 },
  {
    id: 'ghoul',
    name: 'Goule',
    baseHp: 9,
    baseGold: 6,
    zone: 3,
    minDay: 1,
    effects: [{ kind: 'revive', percent: 0.25 }],
  },
  {
    id: 'vampire',
    name: 'Vampire',
    baseHp: 9,
    baseGold: 6,
    zone: 3,
    minDay: 2,
    effects: [{ kind: 'regen', percentPerSecond: 0.05 }],
  },
  {
    id: 'blackCrow',
    name: 'Corbeau noir',
    baseHp: 11,
    baseGold: 7,
    zone: 3,
    minDay: 3,
    effects: [{ kind: 'timeDrain', chancePerSecond: 0.33, seconds: 0.5 }],
  },
  {
    id: 'vampireLord',
    name: 'Saigneur Vampire',
    baseHp: 48,
    baseGold: 50,
    zone: 3,
    minDay: 6,
    boss: true,
    // v0.49.0 (COMMENTS v0.48.1 §1.8).
    effects: [
      {
        kind: 'halveBasicChance',
        chance: BALANCE.vampireLordHalveBasicChance,
        divisor: BALANCE.vampireLordHalveBasicDivisor,
      },
    ],
  },
];

/** Zone 4 — Caverne des Araignées. */
const ZONE_4: EnemyDef[] = [
  { id: 'spiderLarva', name: "Larve d'araignée", baseHp: 4, baseGold: 3, zone: 4, minDay: 1 },
  {
    id: 'spider',
    name: 'Araignée',
    baseHp: 7,
    baseGold: 5,
    zone: 4,
    minDay: 1,
    effects: [{ kind: 'slowThrow', percent: BALANCE.spiderSlowPercent }],
  },
  {
    id: 'mygale',
    name: 'Mygale',
    baseHp: 9,
    baseGold: 6,
    zone: 4,
    minDay: 2,
    effects: [{ kind: 'critDivide', divisor: 2 }],
  },
  { id: 'tarantula', name: 'Tarentule', baseHp: 20, baseGold: 10, zone: 4, minDay: 3 },
  {
    id: 'blackWidow',
    name: 'Veuve Noire',
    baseHp: 48,
    baseGold: 50,
    zone: 4,
    minDay: 6,
    boss: true,
    // v0.49.0 (COMMENTS v0.48.1 §1.8).
    effects: [
      { kind: 'slowThrow', percent: BALANCE.blackWidowSlowPercent },
    ],
  },
];

/**
 * Zone 5 — Col de la montagne.
 *
 * La ligne « Orc Guerrier » de la table du GD n'a **pas** de condition
 * d'apparition (cellule manquante) : elle est lue comme **jour 1**, à l'image
 * du deuxième ennemi de toutes les autres zones (§F, décision d'implémentation).
 */
const ZONE_5: EnemyDef[] = [
  { id: 'smallOrc', name: 'Petit Orc', baseHp: 4, baseGold: 3, zone: 5, minDay: 1 },
  {
    id: 'orcWarrior',
    name: 'Orc Guerrier',
    baseHp: 7,
    baseGold: 5,
    zone: 5,
    minDay: 1,
    effects: [{ kind: 'stun', chancePerSecond: 0.05, seconds: BALANCE.stunDuration }],
  },
  {
    id: 'orcMage',
    name: 'Orc Mage',
    baseHp: 9,
    baseGold: 6,
    zone: 5,
    minDay: 2,
    effects: [{ kind: 'blockSlots', slots: 1 }],
  },
  {
    id: 'bigOrc',
    name: 'Gros Orc',
    baseHp: 20,
    baseGold: 10,
    zone: 5,
    minDay: 3,
    effects: [{ kind: 'stun', chancePerSecond: 0.1, seconds: BALANCE.stunDuration }],
  },
  {
    id: 'orcChief',
    name: 'Chef des Orcs',
    baseHp: 48,
    baseGold: 50,
    zone: 5,
    minDay: 6,
    boss: true,
    // v0.49.0 (COMMENTS v0.48.1 §1.8).
    effects: [
      {
        kind: 'stunOnHit',
        chance: BALANCE.orcChiefStunChance,
        seconds: BALANCE.orcChiefStunSeconds,
      },
    ],
  },
];

/** Zone 6 — Côte escarpée. */
const ZONE_6: EnemyDef[] = [
  { id: 'eel', name: 'Anguille', baseHp: 4, baseGold: 3, zone: 6, minDay: 1 },
  {
    id: 'moonfish',
    name: 'Poisson Lune',
    baseHp: 7,
    baseGold: 5,
    zone: 6,
    minDay: 1,
    effects: [{ kind: 'explodeFaster', factor: BALANCE.moonfishTimeFactor }],
  },
  {
    id: 'mermaid',
    name: 'Sirène',
    baseHp: 9,
    baseGold: 6,
    zone: 6,
    minDay: 2,
    // v0.50.0 (COMMENTS v0.49.1 §1.1) : 20 % → 15 %.
    effects: [{ kind: 'stun', chancePerSecond: BALANCE.mermaidStunChancePerSecond, seconds: BALANCE.stunDuration }],
  },
  {
    id: 'shark',
    name: 'Requin',
    baseHp: 11,
    baseGold: 7,
    zone: 6,
    minDay: 3,
    effects: [{ kind: 'regen', percentPerSecond: 0.05 }],
  },
  {
    id: 'mermaidQueen',
    name: 'Reine des Sirènes',
    baseHp: 48,
    baseGold: 50,
    zone: 6,
    minDay: 6,
    boss: true,
    // v0.49.0 (COMMENTS v0.48.1 §1.8).
    effects: [
      { kind: 'voidDice', count: BALANCE.mermaidQueenVoidDice },
    ],
  },
];

/** Zone 7 — Territoire des Gobelins. */
const ZONE_7: EnemyDef[] = [
  { id: 'goblin', name: 'Gobelin', baseHp: 4, baseGold: 3, zone: 7, minDay: 1 },
  {
    id: 'goblinCrossbow',
    name: 'Gobelin Arbalétrier',
    baseHp: 7,
    baseGold: 5,
    zone: 7,
    minDay: 1,
    effects: [{ kind: 'critDivide', divisor: 3 }],
  },
  {
    id: 'goblinEngineer',
    name: 'Gobelin Ingénieur',
    baseHp: 9,
    baseGold: 6,
    zone: 7,
    minDay: 2,
    effects: [
      {
        kind: 'engineer',
        seconds: BALANCE.engineerSeconds,
        hpMult: BALANCE.engineerHpMult,
      },
    ],
  },
  { id: 'grobelin', name: 'Grobelin', baseHp: 20, baseGold: 11, zone: 7, minDay: 3 },
  {
    id: 'mechaGoblin',
    name: 'Super-Méca Gobelin',
    baseHp: 48,
    baseGold: 50,
    zone: 7,
    minDay: 6,
    boss: true,
    // v0.49.0 (COMMENTS v0.48.1 §1.8).
    effects: [
      {
        kind: 'armor',
        perHit: BALANCE.mechaGoblinArmorPerHit,
        maxStacks: BALANCE.mechaGoblinArmorMaxStacks,
      },
    ],
  },
];

/** Zone 8 — Désert ardent. */
const ZONE_8: EnemyDef[] = [
  { id: 'scorpion', name: 'Scorpion', baseHp: 7, baseGold: 5, zone: 8, minDay: 1 },
  {
    id: 'snake',
    name: 'Serpent',
    baseHp: 5,
    baseGold: 4,
    zone: 8,
    minDay: 1,
    effects: [{ kind: 'hitLimit', maxHits: BALANCE.serpentMaxHits }],
  },
  {
    id: 'vulture',
    name: 'Vautour',
    baseHp: 9,
    baseGold: 6,
    zone: 8,
    minDay: 2,
    effects: [{ kind: 'critDivide', divisor: 4 }],
  },
  {
    id: 'spikyTurtle',
    name: 'Tortue Epineuse Gigantesque',
    baseHp: 18,
    baseGold: 9,
    zone: 8,
    minDay: 3,
    effects: [{ kind: 'halveBasic', divisor: 2 }],
  },
  {
    id: 'sandWorm',
    name: 'Gros Ver des Sables',
    baseHp: 48,
    baseGold: 50,
    zone: 8,
    minDay: 6,
    boss: true,
    // v0.49.0 (COMMENTS v0.48.1 §1.8).
    effects: [
      {
        kind: 'timeTick',
        every: BALANCE.sandWormTimeTickEvery,
        seconds: BALANCE.sandWormTimeTickSeconds,
      },
    ],
  },
];

/** Zone 9 — Ruines du Château. */
const ZONE_9: EnemyDef[] = [
  { id: 'cursedSoldier', name: 'Soldat maudit', baseHp: 7, baseGold: 5, zone: 9, minDay: 1 },
  {
    id: 'cursedMage',
    name: 'Mage maudit',
    baseHp: 5,
    baseGold: 4,
    zone: 9,
    minDay: 1,
    // v0.50.0 (COMMENTS v0.49.1 §1.1, A100-1) : divise par 2 la part de bonus
    // des sorts (annulait leurs dégâts depuis A31).
    effects: [{ kind: 'halveSpells', divisor: BALANCE.cursedMageSpellDivisor }],
  },
  {
    id: 'fleshAmalgam',
    name: 'Amalgame de chair',
    baseHp: 9,
    baseGold: 6,
    zone: 9,
    minDay: 2,
    effects: [{ kind: 'regen', percentPerSecond: 0.075 }],
  },
  {
    id: 'legMonstrosity',
    name: 'Monstruosité sur pattes',
    baseHp: 20,
    baseGold: 11,
    zone: 9,
    minDay: 3,
    effects: [{ kind: 'revive', percent: 0.33 }],
  },
  {
    id: 'cursedKing',
    name: 'Ancien Roi Maudit',
    baseHp: 48,
    baseGold: 50,
    zone: 9,
    minDay: 6,
    boss: true,
    // v0.49.0 (COMMENTS v0.48.1 §1.8).
    effects: [
      { kind: 'voidDice', count: BALANCE.cursedKingVoidDice },
    ],
  },
];

/** Zone 10 — Monde de la Corruption. */
const ZONE_10: EnemyDef[] = [
  {
    id: 'wereBunny',
    name: 'Lapinou garou',
    baseHp: 4,
    baseGold: 3,
    zone: 10,
    minDay: 1,
    effects: [{ kind: 'dodge', chance: 0.33 }],
  },
  {
    id: 'hungryTeddy',
    name: 'Nounours affamé',
    baseHp: 7,
    baseGold: 5,
    zone: 10,
    minDay: 1,
    effects: [{ kind: 'timeDrain', chancePerSecond: 0.33, seconds: 1 }],
  },
  {
    id: 'carnivorousPlant',
    name: 'Plante carnivore',
    baseHp: 9,
    baseGold: 6,
    zone: 10,
    minDay: 2,
    effects: [{ kind: 'regen', percentPerSecond: 0.075 }],
  },
  {
    id: 'sadisticUnicorn',
    name: 'Licorne sadique',
    baseHp: 18,
    baseGold: 9,
    zone: 10,
    minDay: 3,
    // v0.50.0 (COMMENTS v0.49.1 §1.1) : deux dés Maîtres annulés au hasard par
    // lancer, comme les boss d'A98 (bloquait les emplacements 1 et 2 depuis A88).
    effects: [{ kind: 'voidDice', count: BALANCE.sadisticUnicornVoidDice }],
  },
  {
    id: 'tricorn',
    name: 'Tricorne, La Licorne Maléfique',
    baseHp: 48,
    baseGold: 50,
    zone: 10,
    minDay: 6,
    boss: true,
    // v0.49.0 (COMMENTS v0.48.1 §1.8) : trois effets cumulés.
    effects: [
      { kind: 'voidDice', count: BALANCE.tricornVoidDice },
      {
        kind: 'armor',
        perHit: BALANCE.tricornArmorPerHit,
        maxStacks: BALANCE.tricornArmorMaxStacks,
      },
      {
        kind: 'halveBasicChance',
        chance: BALANCE.tricornHalveBasicChance,
        divisor: BALANCE.tricornHalveBasicDivisor,
      },
    ],
  },
];

/** Construit une zone : son boss est le seul ennemi marqué `boss`. */
function zone(level: number, name: string, enemies: EnemyDef[]): ZoneDef {
  const boss = enemies.find((e) => e.boss);
  return { level, name, enemies, bossId: (boss ?? enemies[enemies.length - 1]).id };
}

/** Les 10 zones, dans l'ordre des niveaux d'expédition. */
export const ZONES: readonly ZoneDef[] = [
  zone(1, 'Alentours du Village', ZONE_1),
  zone(2, 'Cimetière de la forêt', ZONE_2),
  zone(3, 'Forêt aux vampires', ZONE_3),
  zone(4, 'Caverne des Araignées', ZONE_4),
  zone(5, 'Col de la montagne', ZONE_5),
  zone(6, 'Côte escarpée', ZONE_6),
  zone(7, 'Territoire des Gobelins', ZONE_7),
  zone(8, 'Désert ardent', ZONE_8),
  zone(9, 'Ruines du Château', ZONE_9),
  zone(10, 'Monde de la Corruption', ZONE_10),
];

/** Les 50 ennemis, à plat (toutes zones confondues). */
export const ENEMIES: EnemyDef[] = ZONES.flatMap((z) => z.enemies);

export const ENEMY_BY_ID: Record<EnemyTypeId, EnemyDef> = Object.fromEntries(
  ENEMIES.map((e) => [e.id, e]),
) as Record<EnemyTypeId, EnemyDef>;

/**
 * Zone d'un niveau d'expédition — **A25** : zone N = niveau N, et au-delà du
 * niveau 10 la zone 10 se répète (la difficulté continue de monter par la
 * courbe de PV, pas par le bestiaire).
 */
export function zoneForLevel(level: number): ZoneDef {
  const index = Math.min(Math.max(1, Math.round(level)), ZONES.length) - 1;
  return ZONES[index];
}

/** Boss de l'Invasion du niveau `level` (celui de sa zone — §F). */
export function bossIdForLevel(level: number): EnemyTypeId {
  return zoneForLevel(level).bossId;
}

// ───────────────────────────────────────────────────── Libellés d'interface

/** « 15 % » — pourcentage entier, à la française. */
function pct(value: number): string {
  return `${Math.round(value * 100)} %`;
}

/** « 1,05 » — nombre à la française (virgule décimale). */
function num(value: number): string {
  return String(value).replace('.', ',');
}

/** « 0,5 s » — secondes à la française (virgule décimale, pas de zéro inutile). */
function sec(value: number): string {
  return `${String(value).replace('.', ',')} s`;
}

/**
 * Libellé court de l'effet d'un ennemi, affiché sous son nom dans le HUD
 * (COMMENTS v0.7.0 §3 / §F : « sans quoi les mécaniques seraient invisibles »).
 * Chaîne vide pour un ennemi sans effet.
 */
export function effectLabel(effect: EnemyEffect | undefined | null): string {
  if (!effect) return '';
  switch (effect.kind) {
    case 'revive':
      return `Revit avec ${pct(effect.percent)} de ses PV`;
    case 'dodge':
      return `Esquive ${pct(effect.chance)} des lancers`;
    case 'regen':
      return `Régénère ${pct(effect.percentPerSecond)} de sa vie par seconde`;
    case 'shield':
      return 'Annule les dégâts du premier dé Maître';
    case 'timeDrain':
      return `${pct(effect.chancePerSecond)} par seconde de retirer ${sec(effect.seconds)} d'expédition`;
    case 'slowThrow':
      return `Réduit votre vitesse de lancer de ${pct(effect.percent)}`;
    case 'critDivide':
      return `Divise les dégâts critiques par ${effect.divisor}`;
    case 'stun':
      return `${pct(effect.chancePerSecond)} par seconde de vous assommer ${sec(effect.seconds)}`;
    case 'blockSlots':
      if (effect.slots <= 1) return "Le dé Maître de votre emplacement 1 n'est plus joué";
      if (effect.slots === 2) return 'Les dés Maîtres de vos emplacements 1 et 2 ne sont plus joués';
      return `Les dés Maîtres de vos emplacements 1 à ${effect.slots} ne sont plus joués`;
    case 'explodeFaster':
      return `Explose en mourant : le temps défile ${pct(effect.factor - 1)} plus vite`;
    case 'engineer':
      return `À sa mort : −${sec(effect.seconds)}, ×${String(effect.hpMult).replace('.', ',')} PV du suivant, ou rien`;
    case 'hitLimit':
      return `Doit mourir en ${effect.maxHits} coups, sinon le temps restant est divisé par 2`;
    case 'halveBasic':
      return `Divise par ${effect.divisor} les dégâts de vos dés Basiques`;
    // v0.50.0 — Mage Maudit (A100-1).
    case 'halveSpells':
      return `Divise par ${num(effect.divisor)} les effets des sorts`;
    // v0.49.0 — effets des boss (COMMENTS v0.48.1 §1.8).
    case 'halveBasicChance':
      return `${pct(effect.chance)} de chance de diviser par ${effect.divisor} les dégâts de vos dés Basiques`;
    case 'stunOnHit':
      return `${pct(effect.chance)} de chance de vous assommer ${sec(effect.seconds)} à chaque lancer`;
    case 'voidDice':
      return effect.count <= 1
        ? 'Annule au hasard un de vos dés Maîtres à chaque lancer'
        : `Annule au hasard ${effect.count} de vos dés Maîtres à chaque lancer`;
    case 'armor':
      return `Divise les dégâts du lancer par ${num(effect.perHit)} par coup reçu, cumulable ${effect.maxStacks} fois`;
    case 'timeTick':
      return `Retire ${sec(effect.seconds)} au chrono toutes les ${sec(effect.every)}`;
  }
}

/**
 * Libellés de **tous** les effets d'un ennemi, dans l'ordre (v0.49.0) : un
 * libellé par effet, les chaînes vides écartées. `[]` sans effet.
 */
export function effectLabels(effects: readonly EnemyEffect[] | undefined | null): string[] {
  if (!effects) return [];
  return effects.map((e) => effectLabel(e)).filter((label) => label.length > 0);
}
